import { useEffect, useRef, useState } from 'preact/hooks'
import type { ResolveResult } from '../shared/types'
import { clearHistory, type HistoryEntry, hostOf, pushHistory, readHistory, resolve } from './lib'
import { Mark } from './Mark'
import { Result } from './Result'

export function Home() {
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ResolveResult | null>(null)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const abort = useRef<AbortController | null>(null)

  useEffect(() => setHistory(readHistory()), [])

  async function run(raw: string) {
    const value = raw.trim()
    if (!value) return

    abort.current?.abort()
    const controller = new AbortController()
    abort.current = controller

    setBusy(true)
    setError(null)
    try {
      const res = await resolve(value, controller.signal)
      if (controller.signal.aborted) return
      setResult(res)
      setHistory(pushHistory({ input: value, clean: res.clean }))
    } catch (err) {
      if (controller.signal.aborted) return
      setResult(null)
      setError(err instanceof Error ? err.message : '解析失敗')
    } finally {
      if (!controller.signal.aborted) setBusy(false)
    }
  }

  /** 從剪貼簿讀 —— 手機上這是最常用的路徑。 */
  async function pasteAndRun() {
    try {
      const text = await navigator.clipboard.readText()
      if (!text.trim()) return
      setInput(text.trim())
      await run(text)
    } catch {
      setError('讀不到剪貼簿，請手動貼上')
    }
  }

  return (
    <main class="mx-auto w-full max-w-xl px-4 pt-8 pb-16 sm:pt-14">
      <header class="mb-7 flex items-center gap-3">
        <Mark class="h-11 w-11 shrink-0 rounded-xl" />
        <div>
          <h1 class="text-lg font-semibold tracking-tight">
            <span class="text-indigo-600 dark:text-cyan-300">de</span>Shorten
          </h1>
          <p class="text-xs text-slate-500 dark:text-slate-400">還原你的短網址</p>
        </div>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void run(input)
        }}
        class="mb-4"
      >
        <div class="card flex items-center gap-2 p-2 focus-within:ring-2 focus-within:ring-indigo-500/40">
          <input
            type="url"
            value={input}
            onInput={(e) => setInput((e.target as HTMLInputElement).value)}
            placeholder="貼上短網址…"
            inputMode="url"
            enterkeyhint="go"
            autocomplete="off"
            autocapitalize="off"
            spellcheck={false}
            class="min-w-0 flex-1 bg-transparent px-2.5 py-2 text-sm outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
          />
          <button type="submit" disabled={busy || !input.trim()} class="btn btn-primary shrink-0">
            {busy ? '解析中…' : '還原'}
          </button>
        </div>
      </form>

      <div class="mb-6 flex gap-2">
        <button type="button" onClick={pasteAndRun} class="btn btn-ghost text-xs">
          貼上並還原
        </button>
        {(result || error) && (
          <button
            type="button"
            onClick={() => {
              setResult(null)
              setError(null)
              setInput('')
            }}
            class="btn btn-ghost text-xs"
          >
            清除
          </button>
        )}
      </div>

      {error && (
        <p class="mb-6 rounded-xl border border-red-300/60 bg-red-50 px-3.5 py-2.5 text-sm text-red-700 dark:border-red-400/25 dark:bg-red-400/10 dark:text-red-300">
          {error}
        </p>
      )}

      {result && (
        <section class="mb-8">
          <Result result={result} />
        </section>
      )}

      {history.length > 0 && (
        <section class="mb-8">
          <div class="mb-2 flex items-center justify-between px-1">
            <h2 class="text-xs font-medium tracking-wide text-slate-400 uppercase dark:text-slate-500">
              最近
            </h2>
            <button
              type="button"
              onClick={() => {
                clearHistory()
                setHistory([])
              }}
              class="text-xs text-slate-400 underline-offset-2 hover:underline dark:text-slate-500"
            >
              清空
            </button>
          </div>
          <ul class="card divide-y divide-slate-100 overflow-hidden dark:divide-white/5">
            {history.map((entry) => (
              <li key={entry.clean}>
                <button
                  type="button"
                  onClick={() => {
                    setInput(entry.input)
                    void run(entry.input)
                  }}
                  class="w-full px-3.5 py-2.5 text-left transition hover:bg-slate-50 dark:hover:bg-white/5"
                >
                  <span class="block text-xs text-slate-400 dark:text-slate-500">
                    {hostOf(entry.clean)}
                  </span>
                  <span class="url mt-0.5 block truncate text-slate-600 select-text dark:text-slate-300">
                    {entry.clean}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <IosShortcut />

      <footer class="mt-8 px-1 text-xs leading-relaxed text-slate-400 dark:text-slate-600">
        安裝到主畫面後，Android 的分享選單會出現 deShorten ——
        分享任何連結進來就自動還原、複製、關閉。
      </footer>
    </main>
  )
}

/** iOS 沒有 Web Share Target，只能靠「捷徑」。刻意壓成一個折疊卡。 */
function IosShortcut() {
  const endpoint = `${location.origin}/api/resolve?format=text&url=`

  return (
    <details class="card px-3.5 py-3 text-sm">
      <summary class="cursor-pointer list-none font-medium">
        iOS 用法（Web Share Target 蘋果不支援）
        <span class="ml-1.5 text-xs font-normal text-slate-400 dark:text-slate-500">展開</span>
      </summary>
      <ol class="mt-3 list-decimal space-y-1.5 pl-5 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
        <li>開「捷徑」App → 新增捷徑 → 資訊 → 勾「顯示於分享工作表」，接收類型選「URL」。</li>
        <li>
          加入動作「取得 URL 內容」，網址填：
          <code class="url mt-1 block rounded-lg bg-slate-100 px-2 py-1.5 text-[0.7rem] dark:bg-white/5">
            {endpoint}
            <span class="text-indigo-500 dark:text-cyan-400">［捷徑輸入］</span>
          </code>
          方法 GET。
        </li>
        <li>加入動作「複製到剪貼板」，輸入接上一步的結果。</li>
        <li>命名為 deShorten。之後在任何 App 分享 → 選 deShorten 就直接複製好了。</li>
      </ol>
    </details>
  )
}
